<template>
  <div class="registry-device-selector">
    <!-- Not v-model: the picker's model is nullable for its DataTable filter use, this one is not. -->
    <DeviceGroupMultiSelect
      :modelValue="model"
      :options="deviceOptions"
      :groups="optionGroups"
      :groupsTitle="$t('device_groups.picker.select_by_group')"
      :placeholder="placeholderComputed"
      @update:modelValue="model = $event ?? []"
    />

    <div v-if="memberGroups.length > 0" class="form-text">
      {{ $t("device_groups.picker.hint") }}
    </div>

    <div v-if="hasValidationErrors" class="text-danger small mt-2">
      <FontAwesomeIcon icon="exclamation-triangle" class="me-1" />
      <span
        v-for="(message, deviceId) in validationErrors"
        :key="deviceId"
        v-show="message"
      >
        {{ message }}
      </span>
    </div>

    <div v-if="model.length > 0" class="registry-device-selector__chosen">
      <div class="registry-device-selector__summary">
        <span class="eyebrow-label">
          <FontAwesomeIcon
            icon="circle-check"
            class="registry-device-selector__check"
          />
          {{
            $t("portfolio_instructions.selected_devices_summary", {
              count: model.length,
            })
          }}
        </span>
        <button
          type="button"
          class="btn btn-link btn-sm registry-device-selector__clear"
          @click="clearSelection"
        >
          {{ $t("common.clear_all") }}
        </button>
      </div>
      <div
        v-for="section in chipSections"
        :key="section.key"
        class="registry-device-selector__group-section"
      >
        <div
          v-if="section.title"
          class="registry-device-selector__group-heading"
        >
          <span class="fw-semibold">{{ section.title }}</span>
          <span class="text-muted">{{ section.countLabel }}</span>
        </div>
        <div class="registry-device-selector__list">
          <span
            v-for="deviceId in visibleChips(section)"
            :key="deviceId"
            class="registry-device-selector__chip"
          >
            <TechnologyIcon
              :technology="getDeviceTechnology(deviceId, registryDevices)"
              iconClass="me-1"
            />
            {{ getDeviceName(deviceId, registryDevices) }}
            <button
              type="button"
              class="registry-device-selector__chip-remove"
              :aria-label="
                $t('portfolio_instructions.remove_device', {
                  deviceName: getDeviceName(deviceId, registryDevices),
                })
              "
              @click="removeDevice(deviceId)"
            >
              <FontAwesomeIcon icon="xmark" />
            </button>
          </span>
          <button
            v-if="section.deviceIds.length > section.limit"
            type="button"
            class="registry-device-selector__chip registry-device-selector__chip--more"
            @click="toggleSectionExpanded(section.key)"
          >
            {{
              expandedSections.has(section.key)
                ? $t("device_groups.picker.show_less")
                : $t("device_groups.picker.more", {
                    count: section.deviceIds.length - section.limit,
                  })
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import TechnologyIcon from "@/components/common/TechnologyIcon.vue";
import {
  getDeviceName,
  getDeviceTechnology,
} from "@/dashboard/production_strategy/overview/PPA/ppaDeviceUtils";
import DeviceGroupMultiSelect, {
  type DeviceSelectGroup,
  type DeviceSelectOption,
} from "@/dashboard/production_strategy/device_groups/DeviceGroupMultiSelect.vue";
import type { RegistryDevice } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { groupBy, map, sortBy } from "lodash-es";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

interface ChipSection {
  key: string;
  /** No heading when nothing is grouped: the section is the whole selection. */
  title: string | null;
  countLabel: string;
  deviceIds: string[];
  /** Chips shown before "+N more". */
  limit: number;
}

/** Chips shown per group section before "+N more". */
const CHIPS_PER_SECTION = 6;

/**
 * Picks registry devices. When some of `registryDevices` are grouped, the dropdown opens with a
 * Groups section: ticking a group adds its devices that are in `availableOptions` now (a group is a
 * shortcut, the model stays device ids), and the chosen chips are listed under their groups. Without
 * groups it is a plain device multi-select.
 */
const props = withDefaults(
  defineProps<{
    registryDevices: RegistryDevice[];
    availableOptions: DeviceSelectOption[];
    deviceValidationErrors?: Record<string, string>;
    placeholder?: string;
  }>(),
  {
    deviceValidationErrors: undefined,
    placeholder: undefined,
  }
);

const model = defineModel<string[]>({ required: true });

const { t } = useI18n();

const validationErrors = computed<Record<string, string>>(
  () => props.deviceValidationErrors ?? {}
);
const hasValidationErrors = computed(() =>
  Object.values(validationErrors.value).some((message) => Boolean(message))
);
const placeholderComputed = computed(
  () => props.placeholder ?? t("portfolio_instructions.select_devices")
);

const expandedSections = ref<Set<string>>(new Set());

const deviceOptions = computed<DeviceSelectOption[]>(() =>
  props.availableOptions.map((option) => ({
    ...option,
    technology: getDeviceTechnology(option.value, props.registryDevices),
  }))
);

// The groups these devices are in, ordered by name: an empty group has nothing to pick.
const memberGroups = computed(() =>
  sortBy(
    map(
      groupBy(
        props.registryDevices.filter(
          (device) => device.device_group_id !== null
        ),
        "device_group_id"
      ),
      (members, id) => ({
        id: Number(id),
        label: members[0].device_group_name ?? "",
        values: members.map((device) => String(device.id)),
      })
    ),
    [({ label }) => label.toLocaleLowerCase(), "id"]
  )
);

const optionGroups = computed<DeviceSelectGroup[]>(() =>
  memberGroups.value.map(({ label, values }) => ({ label, values }))
);

const chipSections = computed<ChipSection[]>(() => {
  if (memberGroups.value.length === 0) {
    return [
      {
        key: "other",
        title: null,
        countLabel: "",
        deviceIds: model.value,
        limit: Infinity,
      },
    ];
  }
  const selected = new Set(model.value);
  const sections: ChipSection[] = memberGroups.value
    .map(({ id, label, values: memberIds }) => ({
      key: `group-${id}`,
      title: label,
      memberIds,
      deviceIds: memberIds.filter((deviceId) => selected.has(deviceId)),
      limit: CHIPS_PER_SECTION,
    }))
    .filter(({ deviceIds }) => deviceIds.length > 0)
    .map(({ memberIds, ...section }) => ({
      ...section,
      countLabel: t("device_groups.picker.group_partial_count", {
        selected: section.deviceIds.length,
        count: memberIds.length,
      }),
    }));
  const grouped = new Set(memberGroups.value.flatMap(({ values }) => values));
  const others = model.value.filter((id) => !grouped.has(id));
  if (others.length > 0) {
    sections.push({
      key: "other",
      title: t("device_groups.picker.other_devices"),
      countLabel: others.length.toString(),
      deviceIds: others,
      limit: CHIPS_PER_SECTION,
    });
  }
  return sections;
});

const visibleChips = (section: ChipSection) =>
  expandedSections.value.has(section.key)
    ? section.deviceIds
    : section.deviceIds.slice(0, section.limit);

const toggleSectionExpanded = (key: string) => {
  const next = new Set(expandedSections.value);
  if (!next.delete(key)) next.add(key);
  expandedSections.value = next;
};

const clearSelection = () => {
  if (model.value.length === 0) {
    return;
  }
  model.value = [];
};

const removeDevice = (deviceId: string) => {
  model.value = model.value.filter((id) => id !== deviceId);
};
</script>

<style scoped lang="scss">
.registry-device-selector__chosen {
  margin-top: 12px;
}

.registry-device-selector__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.registry-device-selector__check {
  color: var(--phoenix-success);
}

.registry-device-selector__clear {
  padding: 2px 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--phoenix-primary);
  text-decoration: none;

  &:hover {
    background: var(--phoenix-primary-bg-subtle);
    border-radius: 6px;
    color: var(--phoenix-primary);
  }
}

.registry-device-selector__list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border: 1px dashed var(--phoenix-border-color);
  border-radius: 8px;
  background: var(--phoenix-gray-50);
  min-height: 44px;
}

.registry-device-selector__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px 5px 10px;
  border-radius: 999px;
  background: var(--phoenix-primary-bg-subtle);
  color: var(--phoenix-primary-text-emphasis);
  font-size: 12px;
  font-weight: 600;
  border: 1px solid rgba(var(--phoenix-primary-rgb), 0.18);
}

.registry-device-selector__group-section
  + .registry-device-selector__group-section {
  margin-top: 12px;
}

.registry-device-selector__group-heading {
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 12px;
}

.registry-device-selector__chip--more {
  cursor: pointer;
  background: var(--phoenix-gray-100);
  color: var(--phoenix-secondary-color);
  border-color: var(--phoenix-border-color);
  padding-right: 10px;
}

.registry-device-selector__chip-remove {
  all: unset;
  cursor: pointer;
  display: inline-flex;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  align-items: center;
  justify-content: center;
  color: var(--phoenix-primary-text-emphasis);
  opacity: 0.7;
  font-size: 10px;

  &:hover {
    background: rgba(var(--phoenix-primary-rgb), 0.15);
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: 2px;
    background: rgba(var(--phoenix-primary-rgb), 0.15);
    opacity: 1;
  }
}
</style>
